import React from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Loader from '../../../components/Loader/Loader';
import Widget from '../../../components/Widget/Widget';
import PageTitle from '../../../components/PageTitle/PageTitle';
import { BarChart } from '../../../components/Chart';
import ReportResultType from './ReportResultType/ReportResultType';
import ReportResultResume from './ReportResultResume/ReportResultResume';
import { Report } from '../../../models';
import Icon from '@mdi/react';
import {
    mdiCreditCardOutline,
    mdiDomain,
    mdiPassportBiometric,
    mdiBank,
    mdiCashMultiple,
    mdiCashRefund
} from '@mdi/js';
import { Row, Col } from 'reactstrap';

import '../../../components/TransactionsResume/TransactionsResume.css';
import './ReportResult.css';

const ReportResult = ({ report }) => {
    const { t }    = useTranslation();
    const { user } = useSelector(state => state.auth);

    if (!report)
        return <Loader />;

    const { stats, currency } = report;

    const formatStats      = stats => Object.keys(stats).map(key => ({ ...stats[key], type: key }));
    const formatToBarChart = stats => Object.keys(stats).map(key => (
        { id: key, [t('reports.revenues')]: stats[key].total.effective, [t('reports.fees')]: Report.total_fees_value(stats[key].total) }
    ));

    const sm_size = user.isSuperAdmin() ? 6 : 4;
    const md_size = user.isSuperAdmin() ? 3 : 4;
    const split_col = { sm: 6, md: 3 };

    const partnerNb = stats.resume_partner_custom?.nb ?? 0;
    const proxypayNb = stats.resume_proxypay?.nb ?? 0;
    const hasPartnerCustom = partnerNb > 0;
    const hasProxypaySlice = proxypayNb > 0;

    const render_totals_simple = () => (
        <Row>
            <Col sm={sm_size} md={md_size} className="totalsPrint">
                <Widget
                    title={ t('reports.nb_transactions') }
                    value={ Report.format_amount(stats.resume.nb) }
                />
            </Col>
            <Col sm={sm_size} md={md_size} className="totalsPrint">
                <Widget
                    title={ t('reports.gross_revenue') }
                    value={ Report.format_money(stats.resume.total.effective, currency) }
                />
            </Col>
            <Col sm={sm_size} md={md_size} className="totalsPrint">
                <Widget
                    title={ t('reports.net_revenue') }
                    value={ Report.format_money(stats.resume.total.net, currency) }
                />
            </Col>
        </Row>
    );

    const render_totals_split = () => (
        <>
            <div className="TransactionsResume-sectionHeading">
                <div className="TransactionsResume-sectionTitle">
                    { t('reports.section_summary_title') }
                </div>
                <div className="TransactionsResume-sectionSubtitle">
                    { t('reports.section_summary_subtitle') }
                </div>
            </div>
            <Row className="mb-2 totalsPrint">
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.nb_transactions') }
                        value={ Report.format_amount(stats.resume.nb) }
                    />
                </Col>
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.gross_revenue') }
                        value={ Report.format_money(stats.resume.total.effective, currency) }
                    />
                </Col>
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.net_revenue') }
                        value={ Report.format_money(stats.resume.total.net, currency) }
                    />
                </Col>
            </Row>

            { hasProxypaySlice && (
            <>
                <div className="TransactionsResume-sectionHeading TransactionsResume-sectionHeading--spaced">
                    <div className="TransactionsResume-sectionTitle">
                        { t('transactions.resume.section_proxypay') }
                    </div>
                    <div className="TransactionsResume-sectionSubtitle">
                        { t('transactions.resume.section_proxypay_subtitle') }
                    </div>
                </div>
                <Row className="mb-2 totalsPrint">
                    <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                        <Widget
                            title={ t('transactions.resume.nb_transactions_proxypay') }
                            value={ Report.format_amount(stats.resume_proxypay.nb) }
                        />
                    </Col>
                    <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                        <Widget
                            title={ t('reports.revenues_proxypay') }
                            value={ Report.format_money(stats.resume_proxypay.total.effective, currency) }
                        />
                    </Col>
                    <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                        <Widget
                            title={ t('transactions.resume.fees_total_proxypay') }
                            value={ Report.format_money(Report.total_fees_value(stats.resume_proxypay.total), currency) }
                        />
                    </Col>
                    <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                        <Widget
                            title={ t('transactions.resume.net_total_proxypay') }
                            value={ Report.format_money(stats.resume_proxypay.total.net, currency) }
                        />
                    </Col>
                </Row>
            </>
            ) }

            <div className="TransactionsResume-sectionHeading TransactionsResume-sectionHeading--spaced">
                <div className="TransactionsResume-sectionTitle">
                    { t('transactions.resume.section_partner_contracts') }
                </div>
                <div className="TransactionsResume-sectionSubtitle">
                    { t('transactions.resume.section_partner_contracts_subtitle') }
                </div>
            </div>
            <Row className="mb-2 totalsPrint">
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.nb_transactions_partner_aggregate') }
                        value={ Report.format_amount(stats.resume_partner_custom.nb) }
                    />
                </Col>
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.revenues_partner_aggregate') }
                        value={ Report.format_money(stats.resume_partner_custom.total.effective, currency) }
                    />
                </Col>
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint">
                    <Widget
                        title={ t('reports.fees') }
                        value={ Report.format_money(Report.total_fees_value(stats.resume_partner_custom.total), currency) }
                    />
                </Col>
                <Col sm={split_col.sm} md={split_col.md} className="totalsPrint TransactionsResume-netUnavailable">
                    <Widget
                        title={ t('reports.net_revenue') }
                        value={ t('transactions.resume.net_total_contract_dependent') }
                    />
                </Col>
            </Row>

        </>
    );

    return (
        <div className="ReportResult">
            { hasPartnerCustom ? render_totals_split() : render_totals_simple() }
            <Row>
                <Col sm={sm_size} md={md_size} className="payReimbprint">
                    <PageTitle
                        title={ t('reports.payments') }
                        icon={ <Icon path={ mdiCashMultiple } color="white" /> }
                        headingSize="3"
                    />
                    <ReportResultResume stats={ stats.payments } currency={ currency } />
                </Col>
                <Col sm={sm_size} md={md_size} className="payReimbprint">
                    <PageTitle
                        title={ t('reports.refunds') }
                        icon={ <Icon path={ mdiCashRefund } color="white" /> }
                        headingSize="3"
                    />
                    <ReportResultResume stats={ stats.refunds } currency={ currency } />
                </Col>
            </Row>
            <Row>
                <Col sm="12">
                    <PageTitle
                        title={ t('reports.card_types') }
                        icon={ <Icon path={ mdiCreditCardOutline } color="white" /> }
                        headingSize="2"
                    />
                    <Card className="reportCard">
                        <CardBody>
                            <Row>
                                <Col sm="12" md="6" className="reportPrintTable">
                                    <ReportResultType stats={ formatStats(stats.card_types) } currency={ currency } />
                                </Col>
                                <Col sm="12" md="6" className="noPrint">
                                    <BarChart
                                        data={ formatToBarChart(stats.card_types) }
                                        keys={ [t('reports.revenues'), t('reports.fees')] }
                                        axisBottomTickRotation={ 45 }
                                        axisLeftLegend={ t('reports.amount') }
                                        labelFormat={ value => Report.format_money(value, currency) }
                                        tooltipFormat={ value => Report.format_money(value, currency) }
                                        enableLabel={ false }
                                        theme={{background:'transparent'}}
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col sm="12">
                    <PageTitle
                        title={ t('reports.acquirers') }
                        icon={ <Icon path={ mdiDomain } color="white" /> }
                        headingSize="2"
                    />
                    <Card className="reportCard">
                        <CardBody>
                            <Row>
                                <Col sm="12" md="6" className="reportPrintTable">
                                    <ReportResultType stats={ formatStats(stats.acquirers) } currency={ currency } />
                                </Col>
                                <Col sm="12" md="6" style={{ height: '500px' }} className="noPrint">
                                    <BarChart
                                        data={ formatToBarChart(stats.acquirers) }
                                        keys={ [t('reports.revenues'), t('reports.fees')] }
                                        axisLeftLegend={ t('reports.amount') }
                                        tooltipFormat={ value => Report.format_money(value, currency) }
                                        enableLabel={ false }
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col sm="12">
                    <PageTitle
                        title={ t('reports.payment_systems') }
                        icon={ <Icon path={ mdiBank } color="white" /> }
                        headingSize="2"
                    />
                    <Card className="reportCard">
                        <CardBody>
                            <Row>
                                <Col sm="12" md="6" className="reportPrintTable">
                                    <ReportResultType stats={ formatStats(stats.payment_systems) } currency={ currency } />
                                </Col>
                                <Col sm="12" md="6" style={{ height: '500px' }} className="noPrint">
                                    <BarChart
                                        data={ formatToBarChart(stats.payment_systems) }
                                        keys={ [t('reports.revenues'), t('reports.fees')] }
                                        axisLeftLegend={ t('reports.amount') }
                                        labelFormat={ value => Report.format_money(value, currency) }
                                        tooltipFormat={ value => Report.format_money(value, currency) }
                                        enableLabel={ false }
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
                <Col sm="12">
                    <PageTitle
                        title={ t('reports.devices') }
                        icon={ <Icon path={ mdiPassportBiometric } color="white" /> }
                        headingSize="2"
                    />
                    <Card className="reportCard">
                        <CardBody>
                            <Row>
                                <Col sm="12" md="6" className="reportPrintTable">
                                    <ReportResultType stats={ formatStats(stats.devices) } currency={ currency } />
                                </Col>
                                <Col sm="12" md="6" style={{ height: '500px' }} className="noPrint">
                                    <BarChart
                                        data={ formatToBarChart(stats.devices) }
                                        keys={ [t('reports.revenues'), t('reports.fees')] }
                                        axisBottomTickRotation={ 45 }
                                        axisLeftLegend={ t('reports.amount') }
                                        tooltipFormat={ value => Report.format_money(value, currency) }
                                        enableLabel={ false }
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ReportResult;
